import { useIsIOS } from "@design-system/hooks";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const DIGIT_COUNT = 10;
const STEP_PERCENT = 100 / DIGIT_COUNT; // 10%

type Size = "small" | "medium" | "large";

// Size configurations
const sizeConfig = {
  small: {
    container: "h-[30px] w-[15px] sm:h-[40px] sm:w-[20px] lg:h-[50px] lg:w-[25px]",
    text: "text-[30px] sm:text-[40px] lg:text-[50px]",
    charContainer: "h-[25px] sm:h-[35px] lg:h-[45px]",
    charText: "text-[20px] sm:text-[30px]",
  },
  medium: {
    container: "h-[45px] w-[22px] sm:h-[60px] sm:w-[30px] lg:h-[75px] lg:w-[37px]",
    text: "text-[45px] sm:text-[60px] lg:text-[75px]",
    charContainer: "h-[37px] sm:h-[52px] lg:h-[67px]",
    charText: "text-[30px] sm:text-[45px]",
  },
  large: {
    container: "h-[60px] w-[30px] sm:h-[80px] sm:w-[40px] lg:h-[100px] lg:w-[50px]",
    text: "text-[60px] sm:text-[80px] lg:text-[100px]",
    charContainer: "h-[50px] sm:h-[70px] lg:h-[80px]",
    charText: "text-[40px] sm:text-[60px]",
  },
};

// Format number with K/M suffixes for large numbers
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return num.toString();
}

/** single digit column (0..9) that scrolls */
function ScrollDigit({
  digit,
  active,
  char,
  size = "large",
}: {
  digit: number;
  active: boolean;
  char?: string;
  size?: Size;
}) {
  const nums = Array.from({ length: DIGIT_COUNT }, (_, i) => i) || [0, 0];
  const isIOS = useIsIOS();
  const config = sizeConfig[size];

  // Handle letter display (K, M, etc.)
  if (char) {
    return (
      <div className={`flex ${config.charContainer} items-center justify-center ${config.charText} font-semibold leading-none text-bright-orange`}>
        {char}
      </div>
    );
  }

  // Handle comma display
  if (digit === -1) {
    return (
      <div className={`flex ${config.container} items-center justify-center ${config.text} font-semibold leading-none text-bright-orange`}>
        ,
      </div>
    );
  }

  // If iOS, show final digit without animation
  if (isIOS) {
    return (
      <div className={config.container}>
        <div className={`flex ${config.container} items-center justify-center ${config.text} font-semibold leading-none text-bright-orange`}>
          {digit}
        </div>
      </div>
    );
  }

  return (
    // container height must match the individual number height below
    <div className={`${config.container} overflow-hidden`}>
      <motion.div
        initial={{ y: "0%" }}
        animate={{ y: `-${active ? digit * STEP_PERCENT : 0}%` }}
        transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
        style={{ willChange: "transform" }}
        className="flex flex-col"
      >
        {nums.map((n) => (
          <div
            key={n}
            className={`flex ${config.container} items-center justify-center ${config.text} font-semibold leading-none text-bright-orange`}
            style={{ fontVariantNumeric: "tabular-nums" }}
            aria-hidden
          >
            {n}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

/** full number (split digits) */
export function ScrollNumber({
  number,
  isInView,
  size = "large",
}: {
  number: number;
  isInView: boolean;
  size?: Size;
}) {
  const [elements, setElements] = useState<
    Array<{ type: "digit" | "char"; value: number | string }>
  >([]);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Format number with K/M suffixes
    const formattedNumber = formatNumber(number);
    const elementArray: Array<{
      type: "digit" | "char";
      value: number | string;
    }> = [];

    formattedNumber.split("").forEach((char) => {
      if (/[0-9.]/.test(char)) {
        if (char === ".") {
          elementArray.push({ type: "char", value: "." });
        } else {
          elementArray.push({ type: "digit", value: Number(char) });
        }
      } else {
        elementArray.push({ type: "char", value: char });
      }
    });

    setElements(elementArray);
  }, [number]);

  return (
    <div ref={ref} className="flex w-fit items-end justify-center" dir="ltr">
      {elements.map((element, i) => (
        <ScrollDigit
          key={i}
          digit={element.type === "digit" ? (element.value as number) : 0}
          active={isInView}
          char={element.type === "char" ? (element.value as string) : undefined}
          size={size}
        />
      ))}
    </div>
  );
}